import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { FieldDef } from '@core/models';
import { PermissionSlotPipe } from '@modules/jupiter/@pipes/permission-slot.pipe';
import { PermissionSlot } from '@modules/jupiter/@store/types';
import { TagsPanelComponent } from '@shared/components/tags-panel/tags-panel.component';
import { HasPermissionPipe } from '@shared/pipes/has-permission.pipe';
import { PipesModule } from '@shared/pipes/pipes.module';
import { TsComponent } from 'app/modules/jupiter/@components/ts.component';
import { JupiterState } from 'app/modules/jupiter/@store/state';
import { ActionType, Entity } from 'app/modules/jupiter/types';
import isEqual from 'lodash-es/isEqual';
import { debounceTime, noop, tap } from 'rxjs';
import {
  FilterListItemChangeArgs,
  FilterListItemValueModel,
  TsBadgeModule,
  TsButtonModule,
  TsFilterModule,
  TSIconModule,
  TsInputComponent,
  TsMenuComponent,
  TsOverlayComponent,
  TsOverlayModule,
} from 'tsui';
import { TsMenuItem } from 'tsui/lib/ts-menu/ts-menu.component';

@Component({
  selector: 'jupiter-action-bar',
  standalone: true,
  imports: [
    TsButtonModule,
    MatLegacyTooltipModule,
    TsBadgeModule,
    TsOverlayModule,
    MatIconModule,
    MatLegacyCheckboxModule,
    TsFilterModule,
    PipesModule,
    FormsModule,
    NgClass,
    MatLegacyMenuModule,
    TSIconModule,
    TagsPanelComponent,
    AsyncPipe,
    TsInputComponent,
    ReactiveFormsModule,
    TsMenuComponent,
    PermissionSlotPipe,
    HasPermissionPipe,
  ],
  templateUrl: './action-bar.component.html',
  styleUrl: './action-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JupiterActionBarComponent extends TsComponent implements OnInit {
  @Input() selectedRows: Entity[];
  @Input({ required: true, alias: 'columns' }) set _columns(value: string[]) {
    this.columns = value;
    this.columnsMemento = JSON.parse(JSON.stringify(value));
  }
  @Input({ required: true }) fieldDefs: FieldDef[];
  @Input({ required: true, alias: 'filterValueList' }) set _filterValueList(value: FilterListItemValueModel[]) {
    this.filterValueList = value;
    this.filterValueListMemento = JSON.parse(JSON.stringify(value));
  }
  @Input({ alias: 'tagsList' }) set _tagsList(value: string[]) {
    this.tagsList = value;
    this.tagsListMemento = JSON.parse(JSON.stringify(value));
  }
  @Input({ alias: 'search' }) set search(value: string) {
    this.searchInputControl.setValue(value, { emitEvent: false });
  }
  @Output() columnSelectionChanged = new EventEmitter<string[]>();
  @Output() filterApplied = new EventEmitter<FilterListItemValueModel[]>();
  @Output() tagsUpdated = new EventEmitter<string[]>();
  @Output() searchChanged = new EventEmitter<string>();
  @Output() openTagsBulkUpdate = new EventEmitter<void>();
  @Output() viewActionTriggered = new EventEmitter<ActionType>();
  @ViewChild('filtersOverlayBox') public filtersOverlayBox: TsOverlayComponent;

  PermissionSlot = PermissionSlot;
  searchInput = '';
  filterValueList: FilterListItemValueModel[] = [];
  tagsList: string[] = [];
  columns: string[] = [];
  relatedTo$ = this.store.select(JupiterState.getRelatedTo);
  featureSupport$ = this.store.select(JupiterState.getFeatureSupport);
  searchInputControl = new FormControl('');
  viewActions: TsMenuItem[] = [
    {
      label: 'Edit View',
      icon: 'ts_pen',
      disabled: () => false,
      action: () => this.viewActionTriggered.emit('edit'),
    },
    {
      label: 'Duplicate',
      icon: 'ts_copy',
      disabled: () => false,
      action: () => this.viewActionTriggered.emit('duplicate'),
    },
    {
      label: 'Delete View',
      icon: 'ts_trash-alt',
      variant: 'danger',
      disabled: () => {
        return this.store.selectSnapshot(JupiterState.getViews).length === 1;
      },
      action: () => this.viewActionTriggered.emit('delete'),
    },
  ];

  private filterValueListMemento: FilterListItemValueModel[] = [];
  private columnsMemento: string[] = [];
  private tagsListMemento: string[] = [];
  readonly isLoading = signal(false);

  ngOnInit(): void {
    this.searchInputControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(300),
        tap((value) => this.searchChanged.emit(value)),
      )
      .subscribe(noop);
  }

  onColumnSelectClosedByBackdropClick(): void {
    if (!isEqual(this.columns, this.columnsMemento)) {
      this.columnSelectionChanged.emit(this.columns);
    }
  }

  toggleDisplayColumn(fieldName: string, change: MatCheckboxChange): void {
    if (change.checked) {
      this.columns = [...this.columns, fieldName];
    } else {
      this.columns = this.columns.filter((x) => x !== fieldName);
    }
  }

  onFilterClosedByBackdropClick(): void {
    this.filterValueList = JSON.parse(JSON.stringify(this.filterValueListMemento));
  }

  onAddFilterItem(): void {
    this.filterValueList = [...this.filterValueList, { fieldName: null, operator: null, value: null }];
  }

  onFilterDefChanged(filterDefChangeArgs: FilterListItemChangeArgs): void {
    const index = filterDefChangeArgs.index;
    delete filterDefChangeArgs.index;
    this.filterValueList[index] = filterDefChangeArgs;
    this.filterValueList = [...this.filterValueList];
  }

  onFilterRemoved(index: number): void {
    const newState = this.filterValueList?.filter((_, i) => i !== index);
    if (!newState.length) {
      this.onFilterCleared();
    }
    this.filterValueList = newState;
  }

  onFilterCleared(): void {
    this.filterValueList = [];
    this.filterApplied.emit(this.filterValueList);
  }

  onFilterApplied(): void {
    this.filterApplied.emit(this.filterValueList);
    this.filtersOverlayBox.closeOverlay();
  }

  onTagsUpdated(tags: string[]): void {
    this.tagsUpdated.emit(tags);
  }

  onOpenTagsBulkUpdate(): void {
    this.openTagsBulkUpdate.emit();
  }
}
